import { Message } from "@/types";

export const scrollToPreviousUserMessage = async (
  container: HTMLElement,
  messages: Message[],
  currentMessageId: string,
  fetchUserMessagePagination: (
    user_message_id: string,
    direction: "up" | "down",
  ) => Promise<{ message_id: string | null }>,
  cleanMessageArray: () => Promise<void>,
): Promise<string | null> => {
  const currentIndex = messages.findIndex(
    (m) => m.message_id === currentMessageId,
  );
  if (currentIndex === -1) return null;

  let prevUserIndex = -1;
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (!messages[i].is_bot) {
      prevUserIndex = i;
      break;
    }
  }

  if (prevUserIndex === -1) {
    const response = await fetchUserMessagePagination(currentMessageId, "up");
    return response.message_id;
  }

  const targetMessageId = messages[prevUserIndex].message_id;
  const targetEl = container.querySelector(
    `[data-message-id="${targetMessageId}"]`,
  ) as HTMLElement | null;

  if (targetEl) {
    const containerRect = container.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const offset = targetRect.top - containerRect.top;
    const targetScroll =
      container.scrollTop +
      offset -
      container.clientHeight / 2 +
      targetRect.height / 2;
    container.scrollTo({ top: targetScroll, behavior: "smooth" });
    return targetMessageId;
  }
  return null;
};

export const scrollToNextUserMessage = async (
  container: HTMLElement,
  messages: Message[],
  currentMessageId: string,
  fetchUserMessagePagination: (
    user_message_id: string,
    direction: "up" | "down",
    number?: number,
  ) => Promise<{ message_id: string | null }>,
  cleanMessageArray: () => Promise<void>,
): Promise<string | null> => {
  const currentIndex = messages.findIndex(
    (m) => m.message_id === currentMessageId,
  );
  if (currentIndex === -1) return null;

  let nextUserIndex = -1;
  for (let i = currentIndex + 1; i < messages.length; i++) {
    if (!messages[i].is_bot) {
      nextUserIndex = i;
      break;
    }
  }

  if (nextUserIndex === -1) {
    const response = await fetchUserMessagePagination(currentMessageId, "down");
    return response.message_id;
  }

  const targetMessageId = messages[nextUserIndex].message_id;
  const targetEl = container.querySelector(
    `[data-message-id="${targetMessageId}"]`,
  ) as HTMLElement | null;

  if (targetEl) {
    const containerRect = container.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const offset = targetRect.top - containerRect.top;
    let targetScroll =
      container.scrollTop +
      offset -
      container.clientHeight / 2 +
      targetRect.height / 2;

    container.scrollTo({ top: targetScroll, behavior: "smooth" });

    await new Promise((resolve) => setTimeout(resolve, 500));
    const newTargetRect = targetEl.getBoundingClientRect();
    const newOffset = newTargetRect.top - containerRect.top;
    const correction =
      newOffset - container.clientHeight / 2 + targetRect.height / 2;
    if (Math.abs(correction) > 10) {
      targetScroll = container.scrollTop + correction;
      container.scrollTo({ top: targetScroll, behavior: "smooth" });
    }

    return targetMessageId;
  }

  return null;
};

export const scrollToBottomJust = (container: HTMLDivElement | null): void => {
  if (!container) return;
  container.scrollTop = container.scrollHeight;
};

const BOOTSTRAP_SCROLL_RETRY_MS = [0, 50, 150, 300, 600, 1000, 2000];

export const pinScrollToBottom = (
  container: HTMLDivElement | null,
  delaysMs: number[] = BOOTSTRAP_SCROLL_RETRY_MS,
): (() => void) => {
  if (!container) return () => {};

  const pin = () => {
    container.scrollTop = container.scrollHeight;
  };

  pin();
  const timeouts = delaysMs
    .filter((ms) => ms > 0)
    .map((ms) => window.setTimeout(pin, ms));
  const rafId = requestAnimationFrame(() => {
    pin();
    requestAnimationFrame(pin);
  });

  return () => {
    cancelAnimationFrame(rafId);
    timeouts.forEach((id) => window.clearTimeout(id));
  };
};

export const scrollToBottomInitial = (
  container: HTMLDivElement | null,
): Promise<void> => {
  if (!container) return Promise.resolve();

  return new Promise((resolve) => {
    const cancel = pinScrollToBottom(container, [0, 50, 150, 300]);
    window.setTimeout(() => {
      cancel();
      resolve();
    }, 350);
  });
};

export const scrollToMessage = (
  container: HTMLElement,
  messageId: string,
): void => {
  const targetEl = container.querySelector(
    `[data-message-id="${messageId}"]`,
  ) as HTMLElement | null;

  if (targetEl) {
    const containerRect = container.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const offset = targetRect.top - containerRect.top;
    const targetScroll =
      container.scrollTop +
      offset -
      container.clientHeight / 2 +
      targetRect.height / 2;

    container.scrollTo({ top: targetScroll, behavior: "smooth" });
  }
};

export const scrollToBottomSmooth = (
  container: HTMLDivElement | null,
): void => {
  if (!container) return;

  container.scrollTo({
    top: container.scrollHeight,
    behavior: "smooth",
  });
};

export const loadOlderMessagesWithScrollPosition = async (
  container: HTMLDivElement | null,
  loadOlderMessagesFn: () => Promise<any>,
  isSmallScreen: boolean,
  minScrollTop: number = 20,
): Promise<void> => {
  if (!container) return;

  const oldHeight = container.scrollHeight;
  const oldTop = container.scrollTop;

  await loadOlderMessagesFn();

  if (isSmallScreen) {
    await new Promise(requestAnimationFrame);
  }

  const newHeight = container.scrollHeight;
  container.scrollTop = newHeight - oldHeight + oldTop;

  if (isSmallScreen && container.scrollTop < minScrollTop) {
    container.scrollTop = minScrollTop;
  }
};
